import { useCallback } from 'react';

import { CONSENT_ACTIONS, updateConsent } from '../adapters/saddlebag';
import { COMPONENT_NAME } from '../constants';
import { OPERATIONAL_EVENTS } from '../types/events';

import type { Logger } from '@web-platform/global-components-types/utils';

export interface UseConsentActionsOptions {
  /**
   * Callback when consent action completes.
   */
  onComplete: () => void;

  /**
   * Logger for operational events.
   */
  logger: Logger;
}

export interface UseConsentActionsResult {
  /**
   * Accept all consent purposes.
   */
  acceptAll: () => Promise<void>;

  /**
   * Reject all non-essential consent purposes.
   */
  rejectAll: () => Promise<void>;

  /**
   * Log manage settings click (navigation handled by href).
   */
  manageSettings: () => void;
}

/**
 * Hook for handling consent actions.
 */
export function useConsentActions(options: UseConsentActionsOptions): UseConsentActionsResult {
  const { logger, onComplete } = options;

  const acceptAll = useCallback(async () => {
    try {
      await updateConsent({ action: CONSENT_ACTIONS.ACCEPT_ALL });
      logger.logOperationalEvent({
        eventName: OPERATIONAL_EVENTS.CONSENT_ACCEPT_ALL,
        component: COMPONENT_NAME,
      });
      onComplete();
    } catch (err) {
      logger.logWarn({
        message: `Failed to accept all consent: ${err instanceof Error ? err.message : String(err)}`,
        component: COMPONENT_NAME,
      });
      logger.logOperationalEvent({
        eventName: OPERATIONAL_EVENTS.CONSENT_UPDATE_FAILED,
        component: COMPONENT_NAME,
        action: CONSENT_ACTIONS.ACCEPT_ALL,
      });
      onComplete();
    }
  }, [onComplete, logger]);

  const rejectAll = useCallback(async () => {
    try {
      await updateConsent({ action: CONSENT_ACTIONS.REJECT_ALL });
      logger.logOperationalEvent({
        eventName: OPERATIONAL_EVENTS.CONSENT_REJECT_ALL,
        component: COMPONENT_NAME,
      });
      onComplete();
    } catch (err) {
      logger.logWarn({
        message: `Failed to reject all consent: ${err instanceof Error ? err.message : String(err)}`,
        component: COMPONENT_NAME,
      });
      logger.logOperationalEvent({
        eventName: OPERATIONAL_EVENTS.CONSENT_UPDATE_FAILED,
        component: COMPONENT_NAME,
        action: CONSENT_ACTIONS.REJECT_ALL,
      });
      onComplete();
    }
  }, [onComplete, logger]);

  // manageSettings only logs the click — the button has href="/privacy-settings"
  // which navigates the user away, so onComplete is not called.
  const manageSettings = useCallback(() => {
    logger.logOperationalEvent({
      eventName: OPERATIONAL_EVENTS.MANAGE_SETTINGS_CLICKED,
      component: COMPONENT_NAME,
    });
  }, [logger]);

  return { acceptAll, rejectAll, manageSettings };
}
